import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getRevAllNodes, getRevAllNodesWithStatus } from "../../../redux/procedureRevisionSlice";
import { getAllVersionHistories } from "../../../redux/versionHistorySlice";
import { generateAlphanumericSuffix } from "../../../util/AppUtils";

/**
 * useCanvasData Hook
 *
 * Manages canvas data fetching and state management using getRevAllNodes
 * Calculates proper page titles and sibling orders based on node hierarchy
 *
 * @param {Object} params
 * @param {string} params.mode - "add" or "edit"
 * @param {string} params.id - Canvas ID (for edit mode)
 * @param {string} params.partnumber - Part number
 * @param {string} params.revision - Revision
 * @param {string} params.partId - Part ID
 * @param {string} params.revisionId - Revision ID
 * @param {string} params.section_id_ref - Section ID reference
 * @param {string} params.step_id_ref - Step ID reference
 * @param {string} params.substep_id_ref - Substep ID reference (query param)
 * @param {string} params.node_id_ref - Node ID reference (query param)
 *
 * @returns {Object} { loading, canvasData, versionId, procedureId, lastSavedText, pageTitle, breadcrumbText, siblingOrder }
 */
const useCanvasData = ({
  mode,
  id,
  partnumber,
  revision,
  partId,
  revisionId,
  section_id_ref,
  step_id_ref,
  substep_id_ref,
  node_id_ref,
}) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [canvasData, setCanvasData] = useState(null);
  const [versionId, setVersionId] = useState("");
  const [procedureId, setProcedureId] = useState("");
  const [lastSavedText, setLastSavedText] = useState("");
  const [pageTitle, setPageTitle] = useState("Canvas");
  const [breadcrumbText, setBreadcrumbText] = useState("");
  const [siblingOrder, setSiblingOrder] = useState(null);
  const [allNodesData, setAllNodesData] = useState([]);

  const [sectionId, setSectionId] = useState(null);
  const [stepId, setStepId] = useState(null);
  const [substepId, setSubstepId] = useState("");

  const { currentStatus, item_code_type } = useSelector((state) => state.procedureRevision);

  const collectPartDetailsFromCtmData = (sourceData) => {
    if (!sourceData || typeof sourceData !== "object") return [];
    if (Array.isArray(sourceData.part_details)) return sourceData.part_details;
    const ctmData = Array.isArray(sourceData.ctm_data) ? sourceData.ctm_data : [];
    return ctmData.flatMap((item) =>
      Array.isArray(item?.part_details) ? item.part_details : []
    );
  };

  useEffect(() => {
    const fetchCanvasData = async () => {
      try {
        setLoading(true);

        // Fetch all nodes using getRevAllNodes
        const result = await dispatch(
          getRevAllNodesWithStatus({
            partId: partId,
            revisionId: revisionId,
          })
        );

        const nodes = result.payload.data;

        if (nodes) {
          setAllNodesData(nodes);

          // Find current section and step
          const currentSection = nodes.find(
            (section) => section.tree_node_id === section_id_ref
          );

          const currentStep = currentSection?.steps?.find(
            (step) => step.tree_node_id === step_id_ref
          );

          setSectionId(currentSection.section_id_ref);
          setStepId(currentStep.step_id_ref);

          // Calculate canvas count and page title
          let totalCanvasCount = 0;
          let calculatedSiblingOrder = null;

          // Count all canvases across all sections and steps
          // Sort sections by sibling order to ensure correct counting
          const sortedSections = [...nodes].sort((a, b) => {
            const orderA =
              a.sibling_order || a.procedure_tree?.sibling_order || 0;
            const orderB =
              b.sibling_order || b.procedure_tree?.sibling_order || 0;
            return parseInt(orderA) - parseInt(orderB);
          });

          for (const section of sortedSections) {
            if (section.steps && Array.isArray(section.steps)) {
              // Sort steps by sibling order
              const sortedSteps = [...section.steps].sort((a, b) => {
                const orderA =
                  a.sibling_order || a.procedure_tree?.sibling_order || 0;
                const orderB =
                  b.sibling_order || b.procedure_tree?.sibling_order || 0;
                return parseInt(orderA) - parseInt(orderB);
              });

              for (const step of sortedSteps) {
                // Count canvases in step
                if (step.children && Array.isArray(step.children)) {
                  const stepCanvases = step.children.filter(
                    (child) => child.type === "canva"
                  );
                  totalCanvasCount += stepCanvases.length;
                }

                // Count canvases in substeps
                if (step.substeps && Array.isArray(step.substeps)) {
                  // Sort substeps by sibling order
                  const sortedSubsteps = [...step.substeps].sort((a, b) => {
                    const orderA =
                      a.sibling_order || a.procedure_tree?.sibling_order || 0;
                    const orderB =
                      b.sibling_order || b.procedure_tree?.sibling_order || 0;
                    return parseInt(orderA) - parseInt(orderB);
                  });

                  for (const substep of sortedSubsteps) {
                    if (substep.children && Array.isArray(substep.children)) {
                      const substepCanvases = substep.children.filter(
                        (child) => child.type === "canva"
                      );
                      totalCanvasCount += substepCanvases.length;
                    }
                  }
                }
              }
            }
          }

          // Set page title based on mode
          let title;
          if (mode === "edit") {
            // For edit mode, find the current canvas's position in the sequence
            const currentCanvasNumber = findCanvasPosition(nodes, id);
            title = `Canvas ${currentCanvasNumber}`;
          } else {
            // For add mode, use total count + 1
            const canvasNumber = totalCanvasCount + 1;
            title = `Canvas ${canvasNumber}`;
          }
          setPageTitle(title);

          // Calculate sibling order based on context
          if (substep_id_ref) {
            // Case 1: Adding canvas to substep
            const targetSubstep = currentStep?.substeps?.find(
              (substep) => substep.tree_node_id === substep_id_ref
            );
            setSubstepId(targetSubstep?.substep_id_ref);
            if (node_id_ref) {
              // When adding after a specific child node in substep
              const targetNode = findNodeById(nodes, node_id_ref);
              if (targetNode) {
                const targetSiblingOrder =
                  targetNode.sibling_order ||
                  targetNode.procedure_tree?.sibling_order ||
                  0;
                calculatedSiblingOrder = parseInt(targetSiblingOrder, 10) + 1;
              } else {
                calculatedSiblingOrder = 1;
              }
            } else if (
              targetSubstep?.children &&
              Array.isArray(targetSubstep.children)
            ) {
              calculatedSiblingOrder = targetSubstep.children.length + 1;
            } else {
              calculatedSiblingOrder = 1;
            }
          } else if (node_id_ref) {
            // Case 2: Adding canvas after existing child node in step
            const targetNode = findNodeById(nodes, node_id_ref);
            if (targetNode) {
              const targetSiblingOrder =
                targetNode.sibling_order ||
                targetNode.procedure_tree?.sibling_order ||
                0;
              calculatedSiblingOrder = parseInt(targetSiblingOrder, 10) + 1;
            } else {
              calculatedSiblingOrder = 1;
            }
          } else {
            // Case 3: Adding canvas to step
            if (currentStep?.children && Array.isArray(currentStep.children)) {
              calculatedSiblingOrder = currentStep.children.length + 1;
            } else {
              calculatedSiblingOrder = 1;
            }
          }

          setSiblingOrder(calculatedSiblingOrder);

          // Build breadcrumb text
          const sectionIndex = nodes.findIndex(
            (section) => section.tree_node_id === section_id_ref
          );
          const sectionNumber = sectionIndex + 1;

          // Calculate step number by counting all steps across all sections before current section
          // plus the index within the current section
          let totalStepsBeforeCurrentSection = 0;
          for (let i = 0; i < sectionIndex; i++) {
            if (
              nodes[i].steps &&
              Array.isArray(nodes[i].steps)
            ) {
              totalStepsBeforeCurrentSection += nodes[i].steps.length;
            }
          }

          const stepIndexInCurrentSection = currentSection?.steps?.findIndex(
            (step) => step.tree_node_id === step_id_ref
          );
          const stepNumber =
            totalStepsBeforeCurrentSection + stepIndexInCurrentSection + 1;

          let breadcrumb = `${partnumber} > Rev ${revision} > Section ${sectionNumber}`;

          if (substep_id_ref) {
            // Sort substeps by sibling_order to get correct index
            const sortedSubsteps = currentStep?.substeps
              ? [...currentStep.substeps].sort((a, b) => {
                  const orderA =
                    a.sibling_order || a.procedure_tree?.sibling_order || 0;
                  const orderB =
                    b.sibling_order || b.procedure_tree?.sibling_order || 0;
                  const intA = parseInt(orderA, 10);
                  const intB = parseInt(orderB, 10);
                  if (intA !== intB) return intA - intB;
                  const dateA = new Date(a.created_at || 0);
                  const dateB = new Date(b.created_at || 0);
                  return dateB - dateA;
                })
              : [];

            const substepIndex = sortedSubsteps.findIndex(
              (substep) => substep.tree_node_id === substep_id_ref
            );
            const substepLetter = generateAlphanumericSuffix(
              substepIndex >= 0 ? substepIndex : 0
            );
            breadcrumb += ` > Step ${stepNumber} > Substep ${stepNumber}${substepLetter}`;
          } else {
            breadcrumb += ` > Step ${stepNumber}`;
          }

          breadcrumb += ` > ${title}`;
          setBreadcrumbText(breadcrumb);
        }
        // For edit mode, find canvas data from allNodesData (using only getRevAllNodes, no API calls)
        if (mode === "edit" && id) {
          // Try finding the canvas node
          let canvasNode = findNodeById(nodes, id);

          // If not found, search more thoroughly through all children
          if (!canvasNode) {
            // Deep search through all sections, steps, substeps, and children
            for (const section of nodes) {
              if (section.steps && Array.isArray(section.steps)) {
                for (const step of section.steps) {
                  // Check step children
                  if (step.children && Array.isArray(step.children)) {
                    for (const child of step.children) {
                      if (
                        child.type === "canva" &&
                        (child.tree_node_id === id ||
                          child.detail?.canvas_tree_node_id === id ||
                          child.canvas_tree_node_id === id)
                      ) {
                        canvasNode = child;
                        break;
                      }
                    }
                  }

                  // Check substep children
                  if (
                    !canvasNode &&
                    step.substeps &&
                    Array.isArray(step.substeps)
                  ) {
                    for (const substep of step.substeps) {
                      if (substep.children && Array.isArray(substep.children)) {
                        for (const child of substep.children) {
                          if (
                            child.type === "canva" &&
                            (child.tree_node_id === id ||
                              child.detail?.canvas_tree_node_id === id ||
                              child.canvas_tree_node_id === id)
                          ) {
                            canvasNode = child;
                            break;
                          }
                        }
                      }
                      if (canvasNode) break;
                    }
                  }
                  if (canvasNode) break;
                }
              }
              if (canvasNode) break;
            }
          }

          if (canvasNode) {
            // Get version_id_ref from node - check both root level and detail object
            // Based on getRevAllNodes structure, version_id_ref can be at root level
            const versionIdFromNode =
              canvasNode.version_id_ref || canvasNode.detail?.version_id_ref;
            const procedureIdFromNode =
              canvasNode.procedure_tree_ref ||
              canvasNode.detail?.procedure_tree_ref;
            // Set basic fields from the node
            setVersionId(versionIdFromNode || "");
            setProcedureId(procedureIdFromNode || "");

            // Fetch version histories if version ID exists
            if (versionIdFromNode) {
              dispatch(
                getAllVersionHistories({ versionId: versionIdFromNode })
              );
            }

            // Generate last saved text
            const timestamp =
              canvasNode.can_updated_at || canvasNode.can_created_at;
            if (timestamp) {
              const { generateLastSavedText } = require("../../../util/stepUtils");
              const userName = localStorage.getItem("user_name") || "Unknown User";
              setLastSavedText(generateLastSavedText(timestamp, userName));
            }

            const currentData = canvasNode?.detail?.current_canvas_data_json;
            const extractedPartDetails = [
              ...collectPartDetailsFromCtmData(currentData),
              ...collectPartDetailsFromCtmData(canvasNode?.detail),
            ];
            const normalizedCurrentData =
              extractedPartDetails.length > 0
                ? {
                    ...(currentData || {}),
                    part_details: extractedPartDetails,
                  }
                : currentData;

            // Set canvas data from node (no API calls - using only getRevAllNodes data)
            setCanvasData({
              ...canvasNode,
              detail: {
                ...(canvasNode.detail || {}),
                current_canvas_data_json: normalizedCurrentData,
              },
            });
          } else {
          }
        }
      } catch (error) {
        console.error("Error fetching canvas data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (partId && revisionId && section_id_ref && step_id_ref) {
      fetchCanvasData();
    }
  }, [
    mode,
    id,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
    substep_id_ref,
    node_id_ref,
    dispatch,
  ]);

  // Helper function to find node by ID recursively
  // For canvas nodes, also checks detail.canvas_tree_node_id
  const findNodeById = (nodes, nodeId) => {
    for (const section of nodes) {
      if (section.tree_node_id === nodeId) return section;

      if (section.steps && Array.isArray(section.steps)) {
        for (const step of section.steps) {
          if (step.tree_node_id === nodeId) return step;

          if (step.children && Array.isArray(step.children)) {
            for (const child of step.children) {
              if (
                child.tree_node_id === nodeId ||
                child.detail?.canvas_tree_node_id === nodeId ||
                child.canvas_tree_node_id === nodeId
              ) {
                return child;
              }
            }
          }

          if (step.substeps && Array.isArray(step.substeps)) {
            for (const substep of step.substeps) {
              if (substep.tree_node_id === nodeId) return substep;

              if (substep.children && Array.isArray(substep.children)) {
                for (const child of substep.children) {
                  if (
                    child.tree_node_id === nodeId ||
                    child.detail?.canvas_tree_node_id === nodeId ||
                    child.canvas_tree_node_id === nodeId
                  ) {
                    return child;
                  }
                }
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Helper function to find canvas position in the sequence
  const findCanvasPosition = (nodes, canvasId) => {
    let counter = 0;

    // Sort sections by sibling order
    const sortedSections = [...nodes].sort((a, b) => {
      const orderA = a.sibling_order || a.procedure_tree?.sibling_order || 0;
      const orderB = b.sibling_order || b.procedure_tree?.sibling_order || 0;
      return parseInt(orderA) - parseInt(orderB);
    });

    for (const section of sortedSections) {
      if (section.steps && Array.isArray(section.steps)) {
        // Sort steps by sibling order
        const sortedSteps = [...section.steps].sort((a, b) => {
          const orderA =
            a.sibling_order || a.procedure_tree?.sibling_order || 0;
          const orderB =
            b.sibling_order || b.procedure_tree?.sibling_order || 0;
          return parseInt(orderA) - parseInt(orderB);
        });

        for (const step of sortedSteps) {
          // Count attachments in step
          if (step.children && Array.isArray(step.children)) {
            const stepAttachments = step.children.filter(
              (child) => child.type === "canva"
            );

            // Check if our attachment is in this step
            const currentAttachment = stepAttachments.find(
              (child) => child?.detail?.canvas_tree_node_id === canvasId
            );
            if (currentAttachment) {
              // Find the position within this step's attachments
              const positionInStep = stepAttachments.findIndex(
                (child) => child?.detail?.canvas_tree_node_id === canvasId
              );
              return counter + positionInStep + 1;
            }

            counter += stepAttachments.length;
          }

          // Count attachments in substeps
          if (step.substeps && Array.isArray(step.substeps)) {
            // Sort substeps by sibling order
            const sortedSubsteps = [...step.substeps].sort((a, b) => {
              const orderA =
                a.sibling_order || a.procedure_tree?.sibling_order || 0;
              const orderB =
                b.sibling_order || b.procedure_tree?.sibling_order || 0;
              return parseInt(orderA) - parseInt(orderB);
            });

            for (const substep of sortedSubsteps) {
              if (substep.children && Array.isArray(substep.children)) {
                const substepAttachments = substep.children.filter(
                  (child) => child.type === "canva"
                );

                // Check if our attachment is in this substep
                const currentAttachment = substepAttachments.find(
                  (child) => child?.detail?.canvas_tree_node_id === canvasId
                );
                if (currentAttachment) {
                  // Find the position within this substep's attachments
                  const positionInSubstep = substepAttachments.findIndex(
                    (child) => child?.detail?.canvas_tree_node_id === canvasId
                  );
                  return counter + positionInSubstep + 1;
                }

                counter += substepAttachments.length;
              }
            }
          }
        }
      }
    }

    // If not found, return 1 as fallback
    return 1;
  };

  return {
    loading,
    canvasData,
    versionId,
    procedureId,
    lastSavedText,
    pageTitle,
    breadcrumbText,
    siblingOrder,
    allNodesData,
    sectionId,
    stepId,
    substepId,
    currentStatus,
    item_code_type,
  };
};

export default useCanvasData;
